import { useAppDispatch } from 'app/store';
import { useAuth } from 'app/shared-components/auth/AuthProvider';
import {
  suspendPaymentCard,
  closePaymentCard,
  unlockPaymentCard,
  notifyCardCanceled,
  updateCardGroup,
} from 'app/pages/store/CardDataSlice/cardsThunks';
import { triggerGroupUpdate } from 'app/pages/store/CardDataSlice/cardDataSlice';

export interface UseCardActionsOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onLoadingChange?: (loading: boolean) => void;
}

export interface CancelCardParams {
  paymentCardId: string;
}

export interface MoveCardToGroupParams {
  paymentCardId: string;
  cardGroupId: string;
  group?: { id: string; name: string } | null;
}

export const useCardActions = (options: UseCardActionsOptions = {}) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();

  const lockCard = async (paymentCardId: string): Promise<boolean> => {
    options.onLoadingChange?.(true);
    try {
      const result = await dispatch(
        suspendPaymentCard({
          paymentCardId,
        })
      );

      if (result.type.endsWith('/rejected')) {
        options.onError?.(new Error('Failed to lock card'));
        return false;
      }

      // Email notifications are sent via orchestrator from backend (respects user preferences)
      options.onSuccess?.();
      return true;
    } catch (error) {
      options.onError?.(error instanceof Error ? error : new Error('Failed to lock card'));
      return false;
    } finally {
      options.onLoadingChange?.(false);
    }
  };

  const unlockCard = async (paymentCardId: string): Promise<boolean> => {
    options.onLoadingChange?.(true);
    try {
      const result = await dispatch(
        unlockPaymentCard({
          paymentCardId,
        })
      );

      if (result.type.endsWith('/rejected')) {
        options.onError?.(new Error('Failed to unlock card'));
        return false;
      }

      // Email notifications are sent via orchestrator from backend (respects user preferences)
      options.onSuccess?.();
      return true;
    } catch (error) {
      options.onError?.(error instanceof Error ? error : new Error('Failed to unlock card'));
      return false;
    } finally {
      options.onLoadingChange?.(false);
    }
  };

  const cancelCard = async ({ paymentCardId }: CancelCardParams): Promise<boolean> => {
    options.onLoadingChange?.(true);
    try {
      const result = await dispatch(
        closePaymentCard({
          paymentCardId,
        })
      );

      if (result.type.endsWith('/rejected')) {
        options.onError?.(new Error('Failed to cancel card'));
        return false;
      }

      // SEE-186: email the card's users. v2-highnote builds the recipients and
      // message and refuses unless this user just closed the card, so a failed
      // close is not followed by a call that can only be refused. Every cancel
      // entry point sends it: before SEE-186 only the card sheet did, because the
      // tables never passed the card's users (a bug, confirmed by product
      // 2026-10-02). Fire-and-forget, as the per-recipient sends were.
      if (user?.sub) {
        dispatch(notifyCardCanceled({ paymentCardId }));
      }

      options.onSuccess?.();
      return true;
    } catch (error) {
      options.onError?.(error instanceof Error ? error : new Error('Failed to cancel card'));
      return false;
    } finally {
      options.onLoadingChange?.(false);
    }
  };

  const moveCardToGroup = async ({
    paymentCardId,
    cardGroupId,
    group,
  }: MoveCardToGroupParams): Promise<boolean> => {
    options.onLoadingChange?.(true);
    try {
      await dispatch(
        updateCardGroup({
          paymentCardId,
          cardGroupId,
          group,
        })
      );

      // Trigger group update counter so GroupsPage refetches groups with updated counts
      dispatch(triggerGroupUpdate());

      options.onSuccess?.();
      return true;
    } catch (error) {
      options.onError?.(error instanceof Error ? error : new Error('Failed to move card to group'));
      return false;
    } finally {
      options.onLoadingChange?.(false);
    }
  };

  return {
    lockCard,
    unlockCard,
    cancelCard,
    moveCardToGroup,
  };
};
